// ===== Packages =====

import {
    Descendant,
    Node,
} from 'slate';

// ===== Interfaces =====

import {
    IInternalPostLinkPreviewData,
    IInternalPostLinkTarget,
    IReadPostChapterItem,
    IReadPostSectionItem,
} from '../interfaces';

// ===== Constants =====

import {
    DEFAULT_POST_TITLE,
} from '../constants/generalConstants';

// ===== Services =====

import resolveInternalPostLinkTarget from './resolveInternalPostLinkTarget';

interface IResolveInternalPostLinkPreviewDataArgs {
    href: string,
    rootPostTitle?: string,
    rootPostValueNodes?: Descendant[],
    rootPostPreviewValueNodes?: Descendant[],
    chapters?: IReadPostChapterItem[],
}

interface IResolvedInternalPostLinkPreviewScope {
    chapterTitle?: string,
    sectionTitle?: string,
    slateValue?: Descendant[],
    authLocked?: boolean,
}

const INTERNAL_POST_LINK_PREVIEW_FALLBACK_SNIPPET = 'Not included in post preview';
const INTERNAL_POST_LINK_AUTH_LOCKED_FALLBACK_SNIPPET = 'Click to join the mailing list and unlock this section';

/**
 * Normalizes preview text so the hover card reads like a single, compact line
 * even when the target paragraph spans multiple Slate text nodes.
 */
const normalizePreviewText = (value?: string): string => `${value || ''}`
    .replace(/\s+/g, ' ')
    .trim();

const normalizeId = (value?: string): string => `${value || ''}`.trim();

/**
 * Cross-scope link previews sometimes receive serialized Slate JSON without the
 * already-parsed `valueNodes` companion. Parse that payload on demand so the
 * hover card can still recover the destination paragraph text.
 */
const parseSerializedSlateValue = (value?: string): Descendant[] | undefined => {
    if (!value) {
        return undefined;
    }

    try {
        const parsedValue = JSON.parse(value) as unknown;
        return Array.isArray(parsedValue)
            ? parsedValue as Descendant[]
            : undefined;
    } catch (_error) {
        return undefined;
    }
};

/**
 * Walks the Slate subtree until it finds the block carrying the target
 * paragraph id. We keep this recursive and explicit so the lookup is easy to
 * follow during link-preview debugging.
 */
const findParagraphNodeById = ({
    nodes,
    paragraphId,
}: {
    nodes?: Descendant[],
    paragraphId: string,
}): Descendant | null => {
    if (!nodes?.length) {
        return null;
    }

    const directMatch = nodes.find((node) => {
        const candidateParagraphId = normalizeId((node as {
            paragraphId?: string,
        }).paragraphId);
        return candidateParagraphId === paragraphId;
    });
    if (directMatch) {
        return directMatch;
    }

    return nodes.reduce<Descendant | null>((matchingNode, node) => {
        if (matchingNode) {
            return matchingNode;
        }
        const childNodes = Array.isArray((node as {
            children?: Descendant[],
        }).children)
            ? (node as {
                children: Descendant[],
            }).children
            : undefined;
        return findParagraphNodeById({
            nodes: childNodes,
            paragraphId,
        });
    }, null);
};

const getSlateValueForScope = (
    chapter?: IReadPostChapterItem,
    section?: IReadPostSectionItem,
): Descendant[] | undefined => {
    if (section?.valueNodes?.length) {
        return section.valueNodes;
    }

    if (section?.value) {
        return parseSerializedSlateValue(section.value);
    }

    if (section?.previewValueNodes?.length) {
        return section.previewValueNodes;
    }

    if (section?.previewValue) {
        return parseSerializedSlateValue(section.previewValue);
    }

    if (chapter?.valueNodes?.length) {
        return chapter.valueNodes;
    }

    if (chapter?.value) {
        return parseSerializedSlateValue(chapter.value);
    }

    if (chapter?.previewValueNodes?.length) {
        return chapter.previewValueNodes;
    }

    return parseSerializedSlateValue(chapter?.previewValue);
};

const resolveSectionAcrossChapters = ({
    chapters,
    sectionId,
}: {
    chapters?: IReadPostChapterItem[],
    sectionId?: string,
}): {
    chapter?: IReadPostChapterItem,
    section?: IReadPostSectionItem,
} => {
    const normalizedSectionId = normalizeId(sectionId);
    if (!chapters?.length || !normalizedSectionId) {
        return {};
    }

    return chapters.reduce<{
        chapter?: IReadPostChapterItem,
        section?: IReadPostSectionItem,
    }>((resolvedScope, chapter) => {
        if (resolvedScope.section) {
            return resolvedScope;
        }

        const matchingSection = chapter.sections?.find((candidateSection) => (
            candidateSection.id === normalizedSectionId
        ));
        if (!matchingSection) {
            return resolvedScope;
        }

        return {
            chapter,
            section: matchingSection,
        };
    }, {});
};

const resolvePreviewScope = ({
    target,
    chapters,
}: {
    target: IInternalPostLinkTarget,
    chapters?: IReadPostChapterItem[],
}): IResolvedInternalPostLinkPreviewScope => {
    if (!chapters?.length) {
        return {};
    }

    const normalizedChapterId = normalizeId(target.chapterId);
    const normalizedSectionId = normalizeId(target.sectionId);
    let chapter = normalizedChapterId
        ? chapters.find((candidateChapter) => candidateChapter.id === normalizedChapterId)
        : undefined;
    let section = normalizedSectionId && chapter?.sections?.length
        ? chapter.sections.find((candidateSection) => candidateSection.id === normalizedSectionId)
        : undefined;

    if (!section && normalizedSectionId) {
        const crossChapterResolution = resolveSectionAcrossChapters({
            chapters,
            sectionId: normalizedSectionId,
        });
        chapter = crossChapterResolution.chapter || chapter;
        section = crossChapterResolution.section;
    }

    return {
        ...(chapter?.title
            ? {
                chapterTitle: chapter.title,
            }
            : {}),
        ...(section?.title
            ? {
                sectionTitle: section.title,
            }
            : {}),
        ...(getSlateValueForScope(chapter, section)
            ? {
                slateValue: getSlateValueForScope(chapter, section),
            }
            : {}),
        ...(
            chapter?.authLocked
            || section?.authLocked
                ? {
                    authLocked: true,
                }
                : {}
        ),
    };
};

/**
 * Resolves the internal-link hover card copy for a target paragraph. The card
 * can then show the post title, scope titles, and a short text preview instead
 * of falling back to a raw `/book?...` href.
 */
const resolveInternalPostLinkPreviewData = ({
    href,
    rootPostTitle,
    rootPostValueNodes,
    rootPostPreviewValueNodes,
    chapters,
}: IResolveInternalPostLinkPreviewDataArgs): IInternalPostLinkPreviewData | null => {
    const target = resolveInternalPostLinkTarget(href);
    if (!target) {
        return null;
    }

    const resolvedPreviewScope = resolvePreviewScope({
        target,
        chapters,
    });
    const rootSlateValue = rootPostValueNodes?.length
        ? rootPostValueNodes
        : rootPostPreviewValueNodes;
    const targetNode = findParagraphNodeById({
        nodes: resolvedPreviewScope.slateValue || rootSlateValue,
        paragraphId: target.paragraphId,
    });
    const fallbackSnippet = resolvedPreviewScope.authLocked
        ? INTERNAL_POST_LINK_AUTH_LOCKED_FALLBACK_SNIPPET
        : INTERNAL_POST_LINK_PREVIEW_FALLBACK_SNIPPET;
    const snippet = targetNode
        ? normalizePreviewText(Node.string(targetNode))
        : fallbackSnippet;
    const usesFallbackSnippet = !targetNode;
    const usesAuthLockedFallbackSnippet = usesFallbackSnippet && !!resolvedPreviewScope.authLocked;

    return {
        href: target.href,
        paragraphId: target.paragraphId,
        postTitle: normalizePreviewText(rootPostTitle) || DEFAULT_POST_TITLE,
        ...(resolvedPreviewScope.chapterTitle
            ? {
                chapterTitle: resolvedPreviewScope.chapterTitle,
            }
            : {}),
        ...(resolvedPreviewScope.sectionTitle
            ? {
                sectionTitle: resolvedPreviewScope.sectionTitle,
            }
            : {}),
        ...(snippet
            ? {
                snippet,
            }
            : {}),
        ...(usesFallbackSnippet
            ? {
                usesFallbackSnippet: true,
            }
            : {}),
        ...(usesAuthLockedFallbackSnippet
            ? {
                usesAuthLockedFallbackSnippet: true,
            }
            : {}),
    };
};

export default resolveInternalPostLinkPreviewData;
